import { formatMatrixNumber } from '../components/projects/weldParameters/weldParameterCellDisplay'
import type { ParameterKey, AggregateMinAvgMax } from './weldParameterAggregator'
import type { AggKey } from './weldParametersColumnLayout'

/**
 * Per-target filter state for the Welding Parameters matrix.
 *
 * `values === null` → no filter active (everything passes).
 * `values === []`   → active filter, but no options selected → nothing matches.
 * `values === ['Lead']` → only rows whose target value equals 'Lead' pass.
 *
 * `text` is the optional contains/equals search term that the popover surfaces
 * at the top of the dropdown (mirrors `passAnalyticsFilters`).
 */
export interface WeldParameterFilterState {
  values: string[] | null
  text: string
}

export const EMPTY_WP_FILTER: WeldParameterFilterState = { values: null, text: '' }

/**
 * Filter targets are addressed by a stable string key so the matrix layout
 * doesn't have to know the underlying value shape:
 *   - 'pass'                → top-row Pass column
 *   - 'torch'               → top-row Torch column
 *   - `metric:${col}:${agg}`→ Min/Avg/Max sub-column under a parameter group
 */
export type WeldParameterFiltersMap = Record<string, WeldParameterFilterState>

export type FilterTarget =
  | { kind: 'pass' }
  | { kind: 'torch' }
  | { kind: 'metric'; col: ParameterKey; agg: AggKey }

export function targetKey(t: FilterTarget): string {
  if (t.kind === 'pass') return 'pass'
  if (t.kind === 'torch') return 'torch'
  return `metric:${t.col}:${t.agg}`
}

/**
 * Row shape the matrix filters operate on. Both the uploaded view (where
 * `values` is `AggregateMinAvgMax`) and the manual view (where `values` is a
 * partial of the same shape) feed rows of this kind via `toFilterRow(...)`.
 */
export interface WeldParameterFilterRow {
  pass: string
  torch: string | null
  values: Partial<Record<ParameterKey, Partial<AggregateMinAvgMax>>>
}

/**
 * Format a numeric value the same way the matrix body cell renders it. Mirrors
 * `fmtCell` in `WeldParametersUploadedView` so the popover labels match the
 * displayed cell values byte-for-byte.
 */
export function formatStat(value: number | null | undefined): string {
  const formatted = formatMatrixNumber(value)
  return formatted || '\u2014'
}

function readMetricValue(
  row: WeldParameterFilterRow,
  col: ParameterKey,
  agg: AggKey
): number | null {
  const v = row.values?.[col]?.[agg]
  return typeof v === 'number' && Number.isFinite(v) ? v : null
}

/**
 * Compute the filter-key string for one row + target. Used both to build the
 * unique-value option list and to decide whether a row passes a given filter.
 */
export function readFilterKey(row: WeldParameterFilterRow, target: FilterTarget): string {
  if (target.kind === 'pass') return row.pass || ''
  if (target.kind === 'torch') return row.torch ?? '—'
  return formatStat(readMetricValue(row, target.col, target.agg))
}

/**
 * Build the sorted unique-value option list for one filter target. Numeric
 * groups sort ascending by parsed number; string groups sort alphabetically.
 * The em-dash bucket ('—') is always last so empty rows can be included or
 * excluded explicitly.
 */
export function buildFilterOptions(
  rows: WeldParameterFilterRow[],
  target: FilterTarget
): string[] {
  const set = new Set<string>()
  for (const row of rows) {
    set.add(readFilterKey(row, target))
  }
  const arr = Array.from(set).filter((v) => v !== '')
  const dash = arr.filter((v) => v === '—')
  const real = arr.filter((v) => v !== '—')
  const allNumeric = real.every((v) => v !== '' && !Number.isNaN(parseFloat(v)))
  if (allNumeric) {
    real.sort((a, b) => parseFloat(a) - parseFloat(b))
  } else {
    real.sort((a, b) => a.localeCompare(b))
  }
  return [...real, ...dash]
}

export function isFilterActive(filter: WeldParameterFilterState | undefined): boolean {
  if (!filter) return false
  if (filter.values !== null) return true
  if (filter.text.trim() !== '') return true
  return false
}

function rowPassesFilter(
  row: WeldParameterFilterRow,
  targetKeyStr: string,
  filter: WeldParameterFilterState
): boolean {
  const target = parseTargetKey(targetKeyStr)
  if (!target) return true
  const key = readFilterKey(row, target)
  if (filter.values !== null && !filter.values.includes(key)) return false
  const text = filter.text.trim().toLowerCase()
  if (text && !key.toLowerCase().includes(text)) return false
  return true
}

function parseTargetKey(key: string): FilterTarget | null {
  if (key === 'pass') return { kind: 'pass' }
  if (key === 'torch') return { kind: 'torch' }
  if (key.startsWith('metric:')) {
    const [, col, agg] = key.split(':')
    if (!col || !agg) return null
    return { kind: 'metric', col: col as ParameterKey, agg: agg as AggKey }
  }
  return null
}

/**
 * Apply the active filters to a list of rows and return the surviving pass
 * labels in their original order. Inactive filters are ignored. The result
 * preserves the order of `passLabels` so downstream sorting behaviour stays
 * untouched.
 */
export function applyWeldParameterFilters(
  passLabels: string[],
  rowsByPass: Map<string, WeldParameterFilterRow>,
  filters: WeldParameterFiltersMap
): string[] {
  const activeEntries = Object.entries(filters).filter(([, f]) => isFilterActive(f))
  if (activeEntries.length === 0) return passLabels
  return passLabels.filter((pass) => {
    const row = rowsByPass.get(pass)
    if (!row) {
      // No row data for this pass → only show it if every active filter
      // accepts the em-dash bucket (otherwise there's no value to match).
      return activeEntries.every(([, filter]) =>
        filter.values === null
          ? true
          : filter.values.includes('—') || filter.values.includes('')
      )
    }
    return activeEntries.every(([key, filter]) => rowPassesFilter(row, key, filter))
  })
}

/**
 * Read the persisted filters map for a given storage key. Silently returns
 * `{}` when sessionStorage is unavailable or the blob is malformed.
 */
export function readFilters(key: string): WeldParameterFiltersMap {
  if (!key || typeof window === 'undefined') return {}
  try {
    const raw = window.sessionStorage.getItem(key)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return {}
    const out: WeldParameterFiltersMap = {}
    for (const [k, v] of Object.entries(parsed)) {
      if (!v || typeof v !== 'object') continue
      const entry = v as Partial<WeldParameterFilterState>
      const values =
        entry.values === null
          ? null
          : Array.isArray(entry.values)
          ? entry.values.filter((x) => typeof x === 'string')
          : null
      const text = typeof entry.text === 'string' ? entry.text : ''
      out[k] = { values, text }
    }
    return out
  } catch {
    return {}
  }
}

/**
 * Persist the active filters under the given storage key. Inactive entries are
 * stripped so the blob stays compact; if no entries remain, the key is removed
 * entirely.
 */
export function writeFilters(key: string, filters: WeldParameterFiltersMap): void {
  if (!key || typeof window === 'undefined') return
  try {
    const compact: WeldParameterFiltersMap = {}
    for (const [k, v] of Object.entries(filters)) {
      if (isFilterActive(v)) compact[k] = v
    }
    if (Object.keys(compact).length === 0) {
      window.sessionStorage.removeItem(key)
    } else {
      window.sessionStorage.setItem(key, JSON.stringify(compact))
    }
  } catch {
    // sessionStorage may be disabled (Safari private mode etc.); fall through.
  }
}
